// Background queue lanes: a player may wait for 5v5 (the primary mode) while
// queueing for or playing 1v1 and other non-5v5 queues. The backend reports
// the most urgent activity in `status`/`queue_id` and every waiting ticket in
// `queued_in`; this module derives what the dock shows beside the main button.

export type LaneContract = {
  radiant_count: number
  dire_count: number
}

export type LaneQueue = {
  id: string
  label: string
  enabled: boolean
  is_at_cap: boolean
  active_contract: LaneContract
  ban?: unknown | null
}

export type LaneTicket = {
  queue_id: string
  queue_elapsed_seconds?: number | null
}

export type LaneState = {
  status: 'idle' | 'queued' | 'ready_check' | 'in_match'
  queue_id?: string | null
  queued_in?: LaneTicket[] | null
  queues: LaneQueue[]
}

export type BackgroundTicket = {
  queueID: string
  label: string
  elapsedSeconds: number
}

export type LaneOffer = {
  queueID: string
  label: string
}

export type PlayerQueueLanes = {
  // Waiting tickets other than the one the main button already represents.
  background: BackgroundTicket[]
  // Queues of the other lane the player may join right now.
  offers: LaneOffer[]
}

export function isPrimaryLaneContract(contract: LaneContract | null | undefined): boolean {
  return Boolean(contract && contract.radiant_count === 5 && contract.dire_count === 5)
}

function queueLabel(queue: LaneQueue | undefined, queueID: string): string {
  return (queue?.label || queue?.id || queueID).trim()
}

export function resolvePlayerQueueLanes(
  state: LaneState | null | undefined,
  options: { canJoin: boolean; partySizeAllowed?: (queue: LaneQueue) => boolean },
): PlayerQueueLanes {
  const empty: PlayerQueueLanes = { background: [], offers: [] }
  if (!state || state.status === 'idle') return empty

  const queues = state.queues ?? []
  const byID = new Map(queues.map(queue => [queue.id, queue]))
  const tickets = state.queued_in ?? []
  const mainQueueID = state.queue_id ?? ''

  const background = tickets
    .filter(ticket => !(state.status === 'queued' && ticket.queue_id === mainQueueID))
    .map(ticket => ({
      queueID: ticket.queue_id,
      label: queueLabel(byID.get(ticket.queue_id), ticket.queue_id),
      elapsedSeconds: Math.max(0, ticket.queue_elapsed_seconds ?? 0),
    }))

  if (!options.canJoin) return { background, offers: [] }

  const mainQueue = byID.get(mainQueueID)
  if (!mainQueue) return { background, offers: [] }
  const mainPrimary = isPrimaryLaneContract(mainQueue.active_contract)
  // A 5v5 ready-check or match blocks every other ticket; a 5v5 search
  // allows 1v1 on the side, and any 1v1 activity allows a 5v5 search.
  if (mainPrimary && state.status !== 'queued') return { background, offers: [] }

  const otherLaneTaken = tickets.some(ticket => (
    isPrimaryLaneContract(byID.get(ticket.queue_id)?.active_contract) !== mainPrimary
  ))
  if (otherLaneTaken) return { background, offers: [] }

  const offers = queues
    .filter(queue => (
      isPrimaryLaneContract(queue.active_contract) !== mainPrimary &&
      queue.enabled &&
      !queue.is_at_cap &&
      !queue.ban &&
      (options.partySizeAllowed?.(queue) ?? true)
    ))
    .map(queue => ({ queueID: queue.id, label: queueLabel(queue, queue.id) }))
  return { background, offers }
}
